import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSession } from '../../context/SessionContext';
import { ApiSystem } from '../../state/types';
import {
  buildConfigsList,
  buildNewConfig,
  getUniqueConfigName,
  isDuplicateSystemCode,
  validateCanSaveSystem
} from '../../components/system/systemHelpers';
import { useSystemsApi } from '../../api/useSystemsApi';

export type SystemSection =
  | "clientProcess"
  | "assetProcessing"
  | "assetStorage"
  | "assetSyndication"
  | "serviceDefinitions"
  | "systemInfo"
  | "createSystem";

export const useSystemsState = () => {
  const {
    systems,
    setSystems,
    selectedSystem,
    selectSystem,
    setIsLoading
  } = useSession();

  const {
    createSystemApi,
    updateSystemApi,
    deleteSystemApi
  } = useSystemsApi();

  const [formData, setFormData] = useState<any>({});
  const [dirty, setDirty] = useState(false);
  const [selectedSection, setSelectedSection] = useState<SystemSection>("systemInfo");
  const [configs, setConfigs] = useState<any[]>([]);
  const [selectedConfig, setSelectedConfig] = useState<string | null>(null);

  const baseSystem = useMemo(() => (selectedSection === "createSystem" ? formData : selectedSystem || {}), [
    selectedSystem,
    selectedSection,
    formData
  ]);

  const canSave = (selectedSection === "createSystem" && formData.systemCode?.trim() && formData.commonName?.trim()) || (selectedSection !== "createSystem" && dirty);
  const isLoadingSystem = !Array.isArray(systems) || (selectedSystem && !selectedSystem) || false;

  const selectConfig = (configId: string | null) => {
    if (!configId || configId === selectedConfig) {
      setSelectedConfig(null);
      setSelectedSection("systemInfo");
      return;
    }

    setSelectedConfig(configId);

    if (selectedSection === "systemInfo") {
      setSelectedSection("clientProcess");
    }
  };

  useEffect(() => {
    const nextConfigs = buildConfigsList(selectedSystem);
    setConfigs(nextConfigs);

    if (selectedSystem) {
      const env = process.env.REACT_APP_ENVIRONMENT || 'dev';
      if (selectedSystem.ums_env) {
        setFormData(selectedSystem);
        setDirty(false);
      } else {
        const updatedSystemData = { ...selectedSystem, ums_env: env };
        setFormData(updatedSystemData);
        setDirty(true);
      }
    }
  }, [selectedSystem]);

  useEffect(() => {
    if (
      selectedSection === "systemInfo" ||
      selectedSection === "createSystem" ||
      selectedSection === "serviceDefinitions"
    ) return;

    if (!selectedConfig && configs.length > 0) {
      const defaultCfg = configs.find((c: any) => c.clientProcess?.is_default);
      if (defaultCfg) {
        selectConfig(defaultCfg.clientProcess.name);
      }
    }
  }, [configs, selectedConfig, selectedSection]);

  const handleChange = (key: string, value: any) => {
    setFormData((prev: any) => {
      const updated = structuredClone(prev);
      const keys = key.split(".");
      let obj = updated;

      for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        if (!(k in obj) || typeof obj[k] !== "object" || obj[k] === null) {
          obj[k] = {};
        }
        obj = obj[k];
      }
      obj[keys[keys.length - 1]] = value;

      const isDifferent = JSON.stringify(updated) !== JSON.stringify(baseSystem);
      setDirty(isDifferent);
      return updated;
    });
  };

  const saveChanges = useCallback(async () => {
    const isCreate = selectedSection === "createSystem";
    const env = process.env.REACT_APP_ENVIRONMENT;

    let payload: ApiSystem;
    let newSelectedConfig: string | null = null;

    if (isCreate) {
      const code = formData.systemCode?.trim?.() ?? "";
      if (!code) return;

      if (isDuplicateSystemCode(code, systems)) {
        alert(`A system with the code "${code}" already exists. Please choose a unique code.`);
        return;
      }

      payload = { ...formData, ums_env: env };
    } else {
      const result = validateCanSaveSystem(formData, selectedConfig || "");
      if (!result) return;

      payload = result.updated;
      newSelectedConfig = result.newConfigName ?? null;
    }

    setIsLoading(true);
    try {
      if (isCreate) await createSystemApi(payload);
      else await updateSystemApi(payload);

      setSystems(prev => isCreate
        ? [...prev, payload]
        : prev.map(s => (s.systemCode === payload.systemCode ? payload : s))
      );
      selectSystem(payload);
      setFormData(payload);

      if (isCreate) setSelectedSection("systemInfo");
      setDirty(false);

      if (!isCreate && newSelectedConfig && newSelectedConfig !== selectedConfig) {
        setSelectedConfig(newSelectedConfig);
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    createSystemApi,
    updateSystemApi,
    formData,
    selectedConfig,
    selectedSection,
    selectSystem,
    setSystems,
    systems,
    setIsLoading
  ]);

  const createSystem = () => {
    setSelectedSection("createSystem");
    setFormData({
      systemCode: "",
      unitIndex: "U01",
      commonName: "",
      cloudFrontBaseUrl: "",
      customerDealIdentifier: "",
      dailyAssetReportEmailList: "",
      IANA_timezone: "",
      ums_env: process.env.REACT_APP_ENVIRONMENT,
      configurations: {},
    });
    setConfigs([]);
    setSelectedConfig(null);
    setDirty(false);
  };

  const cancelCreateSystem = () => {
    const nextConfigs = buildConfigsList(selectedSystem);
    setFormData(selectedSystem || {});
    setConfigs(nextConfigs);
    selectConfig(null);
    setDirty(false);
  };

  const deleteSystem = async () => {
    if (!selectedSystem) return;

    const codeToDelete = selectedSystem.systemCode;
    const confirmed = window.confirm(
      `Are you sure you want to delete system "${codeToDelete}"? This action cannot be undone.`
    );
    if (!confirmed) return;

    setIsLoading(true);
    try {
      await deleteSystemApi(codeToDelete);

      setSystems(prevSystems => {
        const remainingSystems = prevSystems.filter(s => s.systemCode !== codeToDelete);
        selectSystem(remainingSystems.length > 0 ? remainingSystems[0] : null);
        return remainingSystems;
      });

      selectConfig(null);
      setDirty(false);
    } catch (err) {
      console.error(err);
      alert("Failed to delete system. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const createConfig = useCallback(async () => {
    if (!formData) return;

    const updatedSystem = structuredClone(formData) as any;
    const existingConfigs = Object.values(updatedSystem.configurations || {});
    const newConfig = buildNewConfig(existingConfigs);

    updatedSystem.configurations = {
      ...(updatedSystem.configurations || {}),
      [newConfig.clientProcess.name]: newConfig,
    };

    setIsLoading(true);
    try {
      await updateSystemApi(updatedSystem);

      setSystems(prev => prev.map(s => (s.systemCode === updatedSystem.systemCode ? updatedSystem : s)));
      selectSystem(updatedSystem);
      setFormData(updatedSystem);
      setDirty(false);
      selectConfig(newConfig.clientProcess.name);
    } catch (err) {
      console.error("Error creating configuration:", err);
      alert("Failed to create configuration. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [formData, setSystems, selectSystem, updateSystemApi, setIsLoading]);

  const duplicateConfig = useCallback(async () => {
    if (!selectedConfig || !formData?.configurations) return;

    const source = formData.configurations[selectedConfig];
    if (!source) {
      console.error("Cannot duplicate — config not found:", selectedConfig);
      return;
    }

    const updatedSystem = structuredClone(formData) as any;
    const baseConfig = structuredClone(source);
    const existingConfigs = Object.values(updatedSystem.configurations || {});
    const baseName = baseConfig?.clientProcess?.name || selectedConfig;
    const uniqueName = getUniqueConfigName(existingConfigs, baseName);

    baseConfig.clientProcess = {
      ...(baseConfig.clientProcess || {}),
      is_default: false,
      is_display_tablet_rooftop: false,
      name: uniqueName,
    };

    updatedSystem.configurations = {
      ...(updatedSystem.configurations || {}),
      [uniqueName]: baseConfig,
    };

    setIsLoading(true);
    try {
      await updateSystemApi(updatedSystem);
      setSystems(prev => prev.map(s => (s.systemCode === updatedSystem.systemCode ? updatedSystem : s)));
      selectSystem(updatedSystem);
      setFormData(updatedSystem);
      setDirty(false);
      selectConfig(uniqueName);
      console.log("✅ Duplicated config:", uniqueName);
    } catch (err) {
      console.error("Error duplicating config:", err);
      alert("Failed to duplicate configuration. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [formData, selectedConfig, updateSystemApi, setSystems, selectSystem, setIsLoading]);

  const deleteConfig = async () => {
    if (!selectedConfig || !formData?.configurations) return;
    if (!window.confirm(`Delete configuration "${selectedConfig}"?`)) return;

    const updatedConfigs = { ...formData.configurations };
    delete updatedConfigs[selectedConfig];

    const updatedSystem = { ...formData, configurations: updatedConfigs };

    setIsLoading(true);
    try {
      await updateSystemApi(updatedSystem);
      setSystems(prev => prev.map(s => (s.systemCode === updatedSystem.systemCode ? updatedSystem : s)));
      selectSystem(updatedSystem);
      setFormData(updatedSystem);
      setDirty(false);
      selectConfig(null);
    } catch (err) {
      console.error(err);
      alert("Failed to delete configuration. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const saveRawSystem = useCallback(async (nextSystem: ApiSystem) => {
    if (!nextSystem?.systemCode) {
      throw new Error("Invalid system payload: missing systemCode");
    }

    setIsLoading(true);
    try {
      await updateSystemApi(nextSystem);
      setSystems((prev) =>
        prev.map((s) => (s.systemCode === nextSystem.systemCode ? nextSystem : s))
      );
      selectSystem(nextSystem);
      setFormData(nextSystem);
      setDirty(false);
    } finally {
      setIsLoading(false);
    }
  }, [selectSystem, setIsLoading, setSystems, updateSystemApi]);

  return {
    formData,
    configs,
    selectedSection,
    selectedConfig,
    dirty,
    canSave,
    isLoadingSystem,
    setFormData,
    setConfigs,
    setDirty,
    selectConfig,
    setSelectedSection,
    handleChange,
    createSystem,
    cancelCreateSystem,
    saveChanges,
    saveRawSystem,
    deleteSystem,
    createConfig,
    duplicateConfig,
    deleteConfig,
  };
};
